// La fiche de chantier, au format des dossiers d'intervention d'IMP TOITURE.
//
// Ce n'est pas une pièce comptable : c'est un document de terrain, qui part sur
// le toit, se donne à l'équipe et se complète au crayon. Sa forme reprend celle
// des fiches déjà établies à la main — page de garde, puis quatre sections
// numérotées : le chantier, les travaux, la préparation, les photos.
//
// Deux partis pris tenus partout :
//
//  1. **Ce que le CRM ignore est montré comme à remplir**, jamais omis ni
//     inventé. Une ligne vide sur le papier se complète sur place ; une ligne
//     absente ne se remarque pas, et l'information manque le jour où elle sert.
//  2. **Les zones d'écriture sont de vraies zones vides.** Une fiche qu'on ne
//     peut pas annoter ne survit pas à une journée de chantier.

import { messageErreur } from '@imp/shared/message-erreur';
import { nomDocumentImprime } from '@imp/shared/nom-document';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useTitreDocument } from '@/lib/titre';
import {
  CHANTIER_STATUT_LABELS,
  type FicheChantier,
  getFicheChantier,
} from '@/modules/chantiers/api';
import { getOrganisation } from '@/modules/settings/api';

const PRINT_CSS = `
@page { size: A4; margin: 0; }
@media print {
  html, body { background: #fff !important; }
  .fiche-toolbar { display: none !important; }
  /* L'enveloppe d'écran ne doit rien ajouter : sa marge suffisait à pousser
     chaque feuille sur une page de plus, et à glisser une page blanche
     derrière chacune. */
  .fiche-ecran { padding: 0 !important; background: #fff !important; }
  .fiche-feuille {
    box-shadow: none !important;
    margin: 0 !important;
    width: 210mm !important;
    height: 297mm !important;
    min-height: 297mm !important;
    break-after: page;
  }
  .fiche-feuille:last-child { break-after: auto; }
  .fiche-bloc { break-inside: avoid; }
}
`;

const A_REMPLIR = '— à compléter sur place —';

/** Les statuts de devis et de facture, tels qu'on les dit. */
const STATUT_DOC: Record<string, string> = {
  brouillon: 'brouillon',
  a_envoyer: 'à envoyer',
  envoye: 'envoyé',
  envoyee: 'envoyée',
  consulte: 'consulté',
  signe: 'SIGNÉ',
  refuse: 'refusé',
  expire: 'expiré',
  annule: 'annulé',
  converti_facture: 'signé et facturé',
  emise: 'émise',
  partiellement_payee: 'partiellement payée',
  payee: 'payée',
  en_retard: 'EN RETARD',
  litige: 'en litige',
  annulee_avoir: 'annulée par avoir',
};

function libelleStatut(brut: string): string {
  return STATUT_DOC[brut] ?? brut.replace(/_/g, ' ');
}

function fmtEuro(v: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);
}

function fmtDate(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d !== undefined && m !== undefined && y !== undefined ? `${d}/${m}/${y}` : iso;
}

function ou(valeur: string | null | undefined, defaut = A_REMPLIR): string {
  const t = (valeur ?? '').trim();
  return t === '' ? defaut : t;
}

/**
 * Les postes de travaux, tirés de la description et des notes.
 *
 * Une ligne par poste : c'est ainsi que les notes de visite sont prises, et
 * c'est ainsi qu'on les coche. Un paragraphe d'un seul tenant devient un poste
 * unique plutôt que d'être découpé au hasard.
 */
function postesTravaux(f: FicheChantier): string[] {
  const brut = [f.chantier.description ?? '', f.chantier.notes ?? ''].join('\n');
  return brut
    .split('\n')
    .map((l) => l.replace(/^[-–•*\d.)\s]+/, '').trim())
    .filter((l) => l !== '');
}

/** Une feuille A4. Le pied de page porte le rappel d'identité, comme sur les fiches établies à la main. */
function Feuille({
  entete,
  pied,
  children,
}: {
  entete: React.ReactNode;
  pied: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="fiche-feuille mx-auto mb-6 flex min-h-[297mm] w-[210mm] flex-col bg-white px-[14mm] py-[12mm] text-neutral-900 shadow-sm">
      <div className="mb-6 flex items-baseline justify-between border-b border-neutral-300 pb-1.5 text-[9px] font-semibold uppercase tracking-wider text-neutral-500">
        {entete}
      </div>
      <div className="flex-1">{children}</div>
      <div className="mt-6 flex items-baseline justify-between border-t border-neutral-300 pt-1.5 text-[8px] uppercase tracking-wider text-neutral-500">
        {pied}
      </div>
    </div>
  );
}

function Section({ numero, titre }: { numero: string; titre: string }) {
  return (
    <h2 className="mb-4 flex items-baseline gap-3">
      <span className="bg-neutral-900 px-2 py-0.5 text-[13px] font-bold text-white">{numero}</span>
      <span className="text-[17px] font-bold">{titre}</span>
    </h2>
  );
}

function Etiquette({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1 text-[8.5px] font-bold uppercase tracking-wider text-neutral-500">
      {children}
    </div>
  );
}

export function FicheChantierPage() {
  const { id } = useParams();
  const ficheQ = useQuery({
    queryKey: ['fiche-chantier', id],
    queryFn: () => getFicheChantier(id ?? ''),
    enabled: Boolean(id),
  });
  const orgQ = useQuery({ queryKey: ['organisation'], queryFn: getOrganisation });

  const f = ficheQ.data;
  useTitreDocument(
    f ? nomDocumentImprime('Fiche chantier', f.chantier.numero, f.client?.nom ?? null) : null,
  );

  if (ficheQ.isLoading) {
    return <p className="text-muted-foreground p-8">Chargement de la fiche…</p>;
  }
  if (ficheQ.isError || !f) {
    return (
      <p className="text-destructive p-8">
        Erreur : {ficheQ.error ? messageErreur(ficheQ.error) : 'chantier introuvable'}
      </p>
    );
  }

  const ch = f.chantier;
  const raison = ou(orgQ.data?.raison_sociale, 'IMP TOITURE');
  const clientCourt = f.client === null ? 'SANS CLIENT' : f.client.nom.toUpperCase();
  const ville = [ch.code_postal, ch.ville].filter((v) => (v ?? '') !== '').join(' ');
  const postes = postesTravaux(f);
  const resteTotal = f.factures.reduce((t, x) => t + (x.reste ?? 0), 0);

  const entete = (
    <>
      <span>{raison}</span>
      <span>
        {ch.numero} · {clientCourt}
      </span>
    </>
  );
  const pied = (
    <>
      <span>Couverture · Charpente · Étanchéité · Zinguerie</span>
      <span>Éditée le {new Date().toLocaleDateString('fr-FR')}</span>
    </>
  );

  return (
    <>
      <style>{PRINT_CSS}</style>

      <div className="fiche-toolbar bg-card sticky top-0 z-10 flex items-center justify-between gap-3 border-b px-4 py-3">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/chantiers/${ch.id}`}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Retour au chantier
          </Link>
        </Button>
        <Button
          size="sm"
          onClick={() => {
            window.print();
          }}
        >
          <Printer className="mr-1.5 h-4 w-4" /> Imprimer / Enregistrer en PDF
        </Button>
      </div>

      <div className="fiche-ecran bg-muted/40 py-6">
        {/* ================= PAGE DE GARDE ================= */}
        <Feuille
          entete={
            <>
              <span>{raison}</span>
              <span className="text-[13px] font-bold tracking-normal text-neutral-900">
                FICHE DE CHANTIER
              </span>
            </>
          }
          pied={pied}
        >
          <div className="flex h-full flex-col justify-center">
            <div className="mb-10 text-[9px] font-semibold uppercase tracking-wider text-neutral-500">
              Dossier d&apos;intervention · interne
            </div>
            <div className="mb-8 text-[11px] font-bold tracking-wider text-neutral-700">
              {ch.numero}
            </div>
            <h1 className="mb-6 max-w-[150mm] text-[34px] font-bold leading-[1.1]">
              {ou(ch.libelle, 'Chantier sans libellé')}
            </h1>
            <p className="mb-16 text-[14px] text-neutral-700">
              {f.client === null ? A_REMPLIR : f.client.nom}
              {ville === '' ? '' : ` — ${ville}`}
            </p>

            <div className="grid grid-cols-3 gap-6 border-t border-neutral-300 pt-4">
              <div>
                <Etiquette>Prestation</Etiquette>
                <div className="text-[13px]">{ou(ch.type_travaux)}</div>
              </div>
              <div>
                <Etiquette>Intervention prévue</Etiquette>
                <div className="text-[13px]">
                  {ch.date_prevue_debut === null ? 'À planifier' : fmtDate(ch.date_prevue_debut)}
                </div>
              </div>
              <div>
                <Etiquette>Statut</Etiquette>
                <div className="text-[13px]">{CHANTIER_STATUT_LABELS[ch.statut] ?? ch.statut}</div>
              </div>
            </div>
          </div>
        </Feuille>

        {/* ================= 01 LE CHANTIER ================= */}
        <Feuille entete={entete} pied={pied}>
          <Section numero="01" titre="Le chantier" />
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-5">
              <div className="fiche-bloc">
                <Etiquette>Client</Etiquette>
                <div className="text-[15px] font-semibold">
                  {f.client === null ? A_REMPLIR : f.client.nom}
                </div>
              </div>
              <div className="fiche-bloc">
                <Etiquette>Coordonnées</Etiquette>
                <div className="space-y-0.5 text-[13px]">
                  {f.client?.telephone === null || f.client?.telephone === undefined ? null : (
                    <div className="font-medium">Tél. {f.client.telephone}</div>
                  )}
                  {f.client?.mobile === null || f.client?.mobile === undefined ? null : (
                    <div className="font-medium">Port. {f.client.mobile}</div>
                  )}
                  {f.client?.email === null || f.client?.email === undefined ? null : (
                    <div className="text-neutral-600">{f.client.email}</div>
                  )}
                  {f.client === null ||
                  (f.client.telephone === null &&
                    f.client.mobile === null &&
                    f.client.email === null) ? (
                    <div className="text-neutral-500">{A_REMPLIR}</div>
                  ) : null}
                </div>
              </div>
              <div className="fiche-bloc">
                <Etiquette>Adresse du chantier</Etiquette>
                <div className="whitespace-pre-line text-[13px] leading-snug">
                  {[ch.adresse_ligne1, ch.adresse_ligne2, ville]
                    .filter((v) => (v ?? '').trim() !== '')
                    .join('\n') || A_REMPLIR}
                </div>
              </div>
              <div className="fiche-bloc">
                <Etiquette>Accès</Etiquette>
                <div className="h-16 border border-dashed border-neutral-300" />
              </div>
            </div>

            <div className="space-y-5">
              <div className="fiche-bloc">
                <Etiquette>Type de travaux</Etiquette>
                <div className="text-[13px] leading-snug">{ou(ch.type_travaux)}</div>
              </div>
              <div className="fiche-bloc">
                <Etiquette>Date d&apos;intervention</Etiquette>
                <div className="text-[13px]">
                  {ch.date_prevue_debut === null
                    ? 'À PLANIFIER — aucune date à l’agenda'
                    : `${fmtDate(ch.date_prevue_debut)}${
                        ch.date_prevue_fin === null ? '' : ` → ${fmtDate(ch.date_prevue_fin)}`
                      }`}
                </div>
              </div>
              <div className="fiche-bloc">
                <Etiquette>Statut</Etiquette>
                <div className="text-[13px]">
                  {CHANTIER_STATUT_LABELS[ch.statut] ?? ch.statut}
                  {ch.avancement_pourcentage > 0
                    ? ` · ${String(ch.avancement_pourcentage)} % réalisé`
                    : ''}
                </div>
              </div>
              <div className="fiche-bloc">
                <Etiquette>Chef de chantier</Etiquette>
                <div className="text-[13px]">{ou(f.chefChantier)}</div>
              </div>
              <div className="fiche-bloc">
                <Etiquette>Chantier n°</Etiquette>
                <div className="text-[13px] font-medium">{ch.numero}</div>
              </div>
              <div className="fiche-bloc">
                <Etiquette>Devis</Etiquette>
                {f.devis.length === 0 ? (
                  <div className="text-[13px] text-neutral-500">Aucun devis rattaché.</div>
                ) : (
                  <div className="space-y-2 text-[13px]">
                    {f.devis.map((d) => (
                      <div key={d.numero}>
                        <div>
                          <span className="font-medium">{d.numero}</span> — {fmtEuro(d.montant)} HT
                          {d.montantTtc === null ? '' : ` / ${fmtEuro(d.montantTtc)} TTC`}
                          {d.tauxTva === null
                            ? ''
                            : ` (TVA ${(d.tauxTva * 100).toLocaleString('fr-FR')} %)`}
                        </div>
                        {d.acomptePourcentage === null || d.acompteMontant === null ? null : (
                          <div className="text-neutral-700">
                            Acompte {d.acomptePourcentage} % : {fmtEuro(d.acompteMontant)}
                          </div>
                        )}
                        <div className="text-neutral-600">{libelleStatut(d.statut)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {f.factures.length > 0 ? (
                <div className="fiche-bloc">
                  <Etiquette>Facturation</Etiquette>
                  <div className="space-y-1 text-[13px]">
                    {f.factures.map((x) => (
                      <div key={x.numero}>
                        <span className="font-medium">{x.numero}</span> — {fmtEuro(x.montant)} TTC
                        {(x.reste ?? 0) > 0.01 ? ` · reste ${fmtEuro(x.reste ?? 0)}` : ' · soldée'}
                      </div>
                    ))}
                    {resteTotal > 0.01 ? (
                      <div className="pt-1 font-semibold">
                        Reste à encaisser : {fmtEuro(resteTotal)}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </Feuille>

        {/* ================= 02 TRAVAUX ================= */}
        <Feuille entete={entete} pied={pied}>
          <Section numero="02" titre="Travaux à réaliser" />
          <p className="mb-5 text-[10px] italic text-neutral-500">
            Liste issue de la description et des notes du chantier — à valider et compléter sur
            place.
          </p>
          {postes.length === 0 ? (
            <ol className="space-y-5">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 shrink-0 text-[12px] font-bold">{i}</span>
                  <span className="h-5 flex-1 border-b border-dotted border-neutral-300" />
                </li>
              ))}
            </ol>
          ) : (
            <ol className="space-y-4">
              {postes.map((p, i) => (
                <li key={p} className="fiche-bloc flex items-start gap-3">
                  <span className="mt-[1px] inline-block h-3.5 w-3.5 shrink-0 border border-neutral-500" />
                  <span className="w-5 shrink-0 text-[12px] font-bold">{i + 1}</span>
                  <span className="flex-1 text-[13px] leading-snug">{p}</span>
                </li>
              ))}
            </ol>
          )}
          <div className="mt-8">
            <Etiquette>Décision prise sur place</Etiquette>
            <div className="h-24 border border-dashed border-neutral-300" />
          </div>
          <p className="mt-4 text-[8.5px] font-bold uppercase tracking-wider text-neutral-500">
            Cocher chaque poste à l&apos;achèvement
          </p>
        </Feuille>

        {/* ================= 03 PRÉPARATION ================= */}
        <Feuille entete={entete} pied={pied}>
          <Section numero="03" titre="Préparation & matériel" />
          <div className="grid grid-cols-2 gap-8">
            <div>
              <Etiquette>À préparer</Etiquette>
              {f.preparation.length === 0 ? (
                <p className="text-[13px] text-neutral-500">Aucune étape configurée.</p>
              ) : (
                <ul className="space-y-2.5 text-[13px]">
                  {f.preparation.map((e) => (
                    <li key={e.label} className="flex items-start gap-2.5">
                      <span
                        className={`mt-[2px] inline-block h-3.5 w-3.5 shrink-0 border ${
                          e.statut === 'done'
                            ? 'border-neutral-900 bg-neutral-900'
                            : 'border-neutral-500'
                        }`}
                        aria-hidden
                      />
                      <span className={e.statut === 'na' ? 'text-neutral-400' : ''}>
                        {e.label}
                        {e.statut === 'na' ? ' — sans objet' : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {f.rendezVous.length > 0 ? (
                <div className="mt-6">
                  <Etiquette>Rendez-vous à l&apos;agenda</Etiquette>
                  <ul className="space-y-1 text-[12px]">
                    {f.rendezVous.map((r) => (
                      <li key={`${r.debut}-${r.titre}`}>
                        {new Date(r.debut).toLocaleString('fr-FR', {
                          weekday: 'short',
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        — {r.titre}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
            <div>
              <Etiquette>Matériel prévu</Etiquette>
              <div className="h-56 border border-dashed border-neutral-300" />
              <p className="mt-1 text-[9px] italic text-neutral-500">
                À remplir depuis le devis et la visite technique.
              </p>
            </div>
          </div>
          <div className="mt-8">
            <Etiquette>Notes / observations terrain</Etiquette>
            <div className="h-40 border border-dashed border-neutral-300" />
          </div>
        </Feuille>

        {/* ================= 04 PHOTOS ================= */}
        <Feuille entete={entete} pied={pied}>
          <Section numero="04" titre="Photos du dossier" />
          {f.photos.length === 0 ? (
            <>
              <p className="mb-5 text-[11px] italic text-neutral-500">
                Aucune photo au dossier. Emplacements à remplir sur place.
              </p>
              <div className="grid grid-cols-2 gap-5">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i}>
                    <div className="flex h-44 items-center justify-center border border-dashed border-neutral-300 text-[10px] font-bold uppercase tracking-wider text-neutral-400">
                      Photo à prendre
                    </div>
                    <div className="mt-1 h-4 border-b border-dotted border-neutral-300" />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-5">
              {f.photos.map((ph, i) => (
                <figure key={ph.id} className="fiche-bloc">
                  {ph.url === null ? (
                    <div className="flex h-44 items-center justify-center border border-dashed border-neutral-300 text-[10px] uppercase text-neutral-400">
                      Image indisponible
                    </div>
                  ) : (
                    <img
                      src={ph.url}
                      alt={ph.legende ?? `Photo ${String(i + 1)}`}
                      className="h-44 w-full border border-neutral-200 object-cover"
                    />
                  )}
                  <figcaption className="mt-1 text-[10px] leading-snug">
                    <span className="font-bold">{String(i + 1).padStart(2, '0')}</span>{' '}
                    {ou(ph.legende, ph.phase)}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </Feuille>
      </div>
    </>
  );
}
